import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { useTranslations } from "../hooks/use-translations";

interface ProgressBarProps {
  title?: string;
  onFinish?: () => void;
  onCancel?: () => void;
  autoStart?: boolean;
  timeout?: number;
  showPercentage?: boolean;
}

export interface ProgressBarHandle {
  start: () => void;
  cancel: () => void;
}

export const ProgressBar = forwardRef<ProgressBarHandle, ProgressBarProps>(
  ({ title, onFinish, onCancel, autoStart = true, timeout = 10_000, showPercentage = false }, ref) => {
    const { translations } = useTranslations();
    const [progress, setProgress] = useState(0);
    const timerRef = useRef<number | null>(null);
    const startAtRef = useRef<number>(0);
    const runningRef = useRef(false);

    const clearTimer = () => {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const cancel = () => {
      clearTimer();

      if (onCancel) {
        onCancel();
      }

      runningRef.current = false;
    };

    const start = useCallback(() => {
      if (timerRef.current != null || runningRef.current) {
        return;
      }

      clearTimer();
      setProgress(0);
      runningRef.current = true;
      startAtRef.current = Date.now();

      const step = 100;

      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startAtRef.current;
        const pct = Math.min(100, (elapsed / timeout) * 100);

        setProgress(pct);

        if (pct >= 100) {
          clearTimer();
          runningRef.current = false;

          if (onFinish) {
            onFinish();
          }
        }
      }, step);
    }, [onFinish, timeout]);

    useImperativeHandle(ref, () => ({ start, cancel }));

    useEffect(() => {
      if (autoStart) {
        start();
      }

      return () => {
        clearTimer();
        runningRef.current = false;
      };
    }, [autoStart, start]);

    const computedWidth = Math.max(0, Math.min(100, progress));

    const displayPercent = Math.round(computedWidth);

    let minWidthPx = `0px`;

    if (computedWidth > 0) {
      minWidthPx = `2px`;
    }

    return (
      <div className="w-full">
        <div className="mb-1 text-xs text-gray-600 dark:text-gray-300">
          <span>{title ?? ``}</span>
          {showPercentage ? (
            <span className="ml-2 text-[10px] text-gray-500 dark:text-gray-400">{displayPercent}%</span>
          ) : null}

          <button
            className="rounded px-1 text-xs font-semibold text-sky-600 hover:text-red-600 dark:text-sky-400 dark:hover:text-red-400 cursor-pointer"
            onClick={cancel}
            type="button"
          >
            {translations.common.cancelAction}
          </button>
        </div>

        <div className="relative h-3 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
          <div
            aria-label={title}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={displayPercent}
            className="block h-3 bg-primary-600 transition-all duration-100 ease-linear"
            data-testid="progress-bar"
            role="progressbar"
            style={{
              width: `${computedWidth}%`,
              minWidth: minWidthPx,
              willChange: `width`,
              backgroundColor: `#ea580c`,
            }}
          />
        </div>
      </div>
    );
  },
);

ProgressBar.displayName = `ProgressBar`;
